<template>
    <div>
        <v-dialog 
            v-model="secondaryUserDialog" 
            transition="dialog-top-transition"
            persistent
            max-width="45%">
            <v-card>
                <v-card-title class="primary darken-1">
                    <div class="white--text">{{ thisUser.id != null ? 'Edit this User' : 'Add a Secondary User to this Project' }}</div>
                    <v-spacer></v-spacer>
					<v-btn 
						icon
						class="ml-3 white--text"
						@click="closeSecondary()">
						<v-icon>mdi-close</v-icon>
					</v-btn>
                </v-card-title>
                <v-card-text class="pt-2">
                    <SecondaryUser 
                        :project="projectDetails"
                        :thisUser="thisUser"
                        :formatThisDate="formatThisDate"
                        :secondaryUserDialog="secondaryUserDialog"
                        :resetCreateNewUser="resetCreateNewUser"
                        @refreshUsers="emitUserRefresh"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog 
            v-model="adminUserDialog" 
            transition="dialog-top-transition"
            persistent
            max-width="55%">
            <v-card>
                <v-card-title class="primary darken-1">
                    <div class="white--text">{{ addUserHeader  }}</div>
                    <v-spacer></v-spacer>
					<v-btn 
						icon
						class="ml-3 white--text"
						@click="closeSecondary()">
						<v-icon>mdi-close</v-icon>
					</v-btn>
                </v-card-title>
                <v-card-text class="pt-2">
                    <AdminUser 
                        :project="projectDetails"
                        :userData="userData"
                        :adminUserDialog="adminUserDialog"
                        :resetCreateNewUser="resetCreateNewUser"
                        @refreshUsers="emitUserRefresh"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <div class="d-flex d-block align-center">
            <div class="sansation label_medium info--text d-inline">Project Users</div>
            <v-spacer></v-spacer>
            <div class="d-inline">
                <v-btn
                    small
                    class="ml-3 d-inline menu_bg_primary primary--text text--darken-1"
                    elevation=0
                    @click="addAdminUser('partner')">
                    Add Partner User                    
                </v-btn>
                <v-btn
                    small
                    class="ml-3 d-inline menu_bg_primary primary--text text--darken-1"
                    elevation=0
                    @click="addAdminUser('owner')">
                    Add Owner                    
                </v-btn>
                <v-btn
                    small
                    class="ml-3 d-inline menu_bg_primary primary--text text--darken-1"
                    elevation=0
                    @click="addSecondaryUser('secondary')">
                    Add Secondary User                    
                </v-btn>
            </div>
        </div>

        <div v-if="!projectOwners.length && !projectSecondaryUsers.length && !projectPartners.length" class="ma-6">
            No additional users to show
        </div>
        
        <div v-if="projectOwners.length" class="mt-12 subtitle-1 info--text d-block">Owners</div>
        <v-data-table
            v-if="projectOwners.length"
            :headers="owner_header"
            :items="projectOwners"
            hide-default-footer
            class="mb-12">
            
            <template v-slot:header="{ headers }">
                <thead>
                    <tr>
                        <td v-for="column in headers" :key="column.value">
                            {{column.text}}
                        </td>
                    </tr>
                </thead>
            </template>

            <template v-slot:body="{ items }">
                <tbody name="list" is="transition-group">
                    <tr v-for="item in items" :key="item.user_id" class="item-row avenir_book">
                        <td class="text-start">
                            {{ item.first_name }} {{ item.last_name }}
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <router-link :to="`/user_details/${item.user_id}`" target="_blank">
                                        <v-icon 
                                            small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on">
                                            mdi-open-in-new
                                        </v-icon>
                                    </router-link>
                                </template>
                                <span>Go to User Details</span>
                            </v-tooltip>
                        </td>
                        <td class="text-start">
                            <div v-if="item.phone">
                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span 
                                            :id="`copy-phone-${item.user_id}`"
                                            class="contact_text_link"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="beginCall(item.phone.trim())">{{item.phone.trim()}}</span>
                                    </template>
                                    <span>Click to Call</span>
                                </v-tooltip>

                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                            x-small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on" 
                                            @click="handleCopyPhone(item.user_id)">
                                            mdi-content-copy
                                        </v-icon>
                                    </template>
                                    <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                                </v-tooltip>
                            </div>
                            <div v-else>No phone on file</div>
                        </td>
                        
                        <td class="text-start">
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <span 
                                        :id="`copy-email-${item.user_id}`"
                                        class="contact_text_link"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click.stop="beginEmail(item.user_email.trim())">{{item.user_email.trim()}}</span>
                                </template>
                                <span>Click to Email</span>
                            </v-tooltip>
                            
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon 
                                        x-small 
                                        class="ml-2 mt-n1 contact_btn_color"
                                        v-bind="attrs"
                                        v-on="on" 
                                        @click="handleCopyEmail(item.user_id)">
                                        mdi-content-copy
                                    </v-icon>
                                </template>
                                <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                            </v-tooltip>
                        </td>

                        <td class="text-start">
                            <div v-if="formatLastLoginDate(item.last_logged_in)">
                                {{ formatLastLoginDate(item.last_logged_in) }}
                            </div>
                        </td>

                        <td class="text-center">
                            <v-icon
                                class="ml-5"
                                small
                                @click="deleteUser(item)">
                                mdi-delete
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-data-table>
        
        <div v-if="projectSecondaryUsers.length" class="mt-12 subtitle-1 info--text d-block">Secondary Users</div>
        <v-data-table
            v-if="projectSecondaryUsers.length"
            :headers="users_header"
            :items="projectSecondaryUsers"
            hide-default-footer>
            
            <template v-slot:header="{ headers }">
                <thead>
                    <tr>
                        <td v-for="column in headers" :key="column.value">
                            {{column.text}}
                        </td>
                    </tr>
                </thead>
            </template>

            <template v-slot:body="{ items }">
                <tbody name="list" is="transition-group">
                    <tr v-for="item in items" :key="item.id" class="item-row avenir_book">
                        <td class="text-start py-1">
                            {{ item.first_name }} {{ item.last_name }}
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <router-link :to="`/user_details/${item.user_id}`" target="_blank">
                                        <v-icon 
                                            small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on">
                                            mdi-open-in-new
                                        </v-icon>
                                    </router-link>
                                </template>
                                <span>Go to User Details</span>
                            </v-tooltip>

                            <div v-if="item.phone" class="d-block ml-2 mt-1">
                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span 
                                            :id="`copy-phone-${item.user_id}`"
                                            class="contact_text_link"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="beginCall(item.phone.trim())">{{item.phone.trim()}}</span>
                                    </template>
                                    <span>Click to Call</span>
                                </v-tooltip>

                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                            x-small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on" 
                                            @click="handleCopyPhone(item.user_id)">
                                            mdi-content-copy
                                        </v-icon>
                                    </template>
                                    <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                                </v-tooltip>
                            </div>

                            <div class="d-block ml-2 mt-1">
                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span 
                                            :id="`copy-email-${item.user_id}`"
                                            class="contact_text_link"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="beginEmail(item.user_email.trim())">{{item.user_email.trim()}}</span>
                                    </template>
                                    <span>Click to Email</span>
                                </v-tooltip>
                                
                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                            x-small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on" 
                                            @click="handleCopyEmail(item.user_id)">
                                            mdi-content-copy
                                        </v-icon>
                                    </template>
                                    <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                                </v-tooltip>
                            </div>
                        </td>
                        <td class="text-start text-capitalize">
                            <v-icon  
                                :class="item.read ? 'light-green--text' : 'error--text text--darken-2'">
                                {{ item.read ? 'mdi-checkbox-marked' : 'mdi-close-box' }}
                            </v-icon>
                        </td>
                        <td class="text-start text-capitalize">
                            <v-icon  
                                :class="item.write ? 'light-green--text' : 'error--text text--darken-2'">
                                {{ item.write ? 'mdi-checkbox-marked' : 'mdi-close-box' }}
                            </v-icon>
                        </td>
                        <td class="text-start text-capitalize">
                            <v-icon  
                                :class="item.deliverables ? 'light-green--text' : 'error--text text--darken-2'">
                                {{ item.deliverables ? 'mdi-checkbox-marked' : 'mdi-close-box' }}
                            </v-icon>
                        </td>
                        <td class="text-start">
                            <div v-if="formatExpirationDate(item.expires)">
                                {{ formatExpirationDate(item.expires) }}
                            </div>
                            <div v-else>
                                <v-tooltip right>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                            v-bind="attrs"
                                            v-on="on">
                                            mdi-infinity
                                        </v-icon>
                                    </template>
                                    <span class="no_expiration">No Expiration</span>
                                </v-tooltip>
                            </div>
                        </td>

                        <td class="text-start">
                            <div v-if="formatLastLoginDate(item.last_logged_in)">
                                {{ formatLastLoginDate(item.last_logged_in) }}
                            </div>
                        </td>

                        <td class="text-start" width="">
                            <v-icon
                                small
                                class="mr-2"
                                @click="editUser(item)">
                                mdi-pencil
                            </v-icon>
                            <v-icon
                                small
                                @click="deleteUser(item)">
                                mdi-delete
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-data-table>
       
        <div v-if="projectPartners.length" class="mt-12 subtitle-1 info--text d-block">Partners</div>
        <v-data-table
            v-if="projectPartners.length"
            :headers="partner_header"
            :items="projectPartners"
            hide-default-footer
            class="mb-12">
            
            <template v-slot:header="{ headers }">
                <thead>
                    <tr>
                        <td v-for="column in headers" :key="column.value">
                            {{column.text}}
                        </td>
                    </tr>
                </thead>
            </template>

            <template v-slot:body="{ items }">
                <tbody name="list" is="transition-group">
                    <tr v-for="item in items" :key="item.user_id" class="item-row avenir_book">
                        <td class="text-start">
                            {{ item.first_name }} {{ item.last_name }}
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <router-link :to="`/user_details/${item.user_id}`" target="_blank">
                                        <v-icon 
                                            small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on">
                                            mdi-open-in-new
                                        </v-icon>
                                    </router-link>
                                </template>
                                <span>Go to User Details</span>
                            </v-tooltip>
                        </td>
                        <td class="text-start">
                            <div v-if="item.phone">
                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span 
                                            :id="`copy-phone-${item.user_id}`"
                                            class="contact_text_link"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="beginCall(item.phone.trim())">{{item.phone.trim()}}</span>
                                    </template>
                                    <span>Click to Call</span>
                                </v-tooltip>

                                <v-tooltip top content-class="contact_tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon 
                                            x-small 
                                            class="ml-2 mt-n1 contact_btn_color"
                                            v-bind="attrs"
                                            v-on="on" 
                                            @click="handleCopyPhone(item.user_id)">
                                            mdi-content-copy
                                        </v-icon>
                                    </template>
                                    <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                                </v-tooltip>
                            </div>
                            <div v-else>No phone on file</div>
                        </td>
                        <td class="text-start">
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <span 
                                        :id="`copy-email-${item.user_id}`"
                                        class="contact_text_link"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click.stop="beginEmail(item.user_email.trim())">{{item.user_email.trim()}}</span>
                                </template>
                                <span>Click to Email</span>
                            </v-tooltip>
                            
                            <v-tooltip top content-class="contact_tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon 
                                        x-small 
                                        class="ml-2 mt-n1 contact_btn_color"
                                        v-bind="attrs"
                                        v-on="on" 
                                        @click="handleCopyEmail(item.user_id)">
                                        mdi-content-copy
                                    </v-icon>
                                </template>
                                <span>{{ trigger_copied ? 'copied' : 'copy' }}</span>
                            </v-tooltip>   
                        </td>

                        <td class="text-start">
                            <div v-if="formatLastLoginDate(item.last_logged_in)">
                                {{ formatLastLoginDate(item.last_logged_in) }}
                            </div>
                        </td>

                        <td class="text-center">
                            <v-icon
                                class="ml-5"
                                small
                                @click="deleteUser(item)">
                                mdi-delete
                            </v-icon>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-data-table>
    </div>
</template>

<script>  
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment';

    import SecondaryUser from './SecondaryUser.vue';
    import AdminUser from './AdminUser.vue';

    export default {
        name: 'projectUsers',

        components: {
        	SecondaryUser,
            AdminUser
		},

        props: {
            projectDetails: Object,
            formatThisDate: Function,
            refreshUsers: Function
        },

        computed: {
            projectOwners () {
                return this.projectDetails.users.filter( user => user.user_type == 'owner' )
            },
            projectSecondaryUsers () {
                return this.projectDetails.users.filter( user => user.user_type == 'secondary' )
            },
            projectPartners () {
                return this.projectDetails.users.filter( user => user.user_type == 'partner' )
            }
        },

        data () {
            return {
                owner_header: [
                    {   text: 'Full Name',  value: 'first_name',        align: 'start', width:'',   sortable: true },
                    {   text: 'Phone',      value: 'phone',             align: 'left',  width:'',   sortable: true },
                    {   text: 'Email',      value: 'user_email',        align: 'left',  width:'',   sortable: true },
                    {   text: 'Last LogIn', value: 'last_logged_in',    align: 'left',  width:'',   sortable: false },
                    {   text: 'Actions',    value: 'actions',           align: 'left',  width:'8%', sortable: false },
                ],
                partner_header: [
                    {   text: 'Full Name',  value: 'first_name',        align: 'start', width:'',   sortable: true },
                    {   text: 'Phone',      value: 'phone',             align: 'left',  width:'',   sortable: true },
                    {   text: 'Email',      value: 'user_email',        align: 'left',  width:'',   sortable: true },
                    {   text: 'Last LogIn', value: 'last_logged_in',    align: 'left',  width:'',   sortable: false },
                    {   text: 'Actions',    value: 'actions',           align: 'left',  width:'8%', sortable: false },
                ],
                users_header: [
                    {   text: 'Full Name',                  value: 'full_name',         align: 'start', width:'',    sortable: true },
                    {   text: 'Can Read Data',              value: 'read',              align: 'left',  width:'16%', sortable: true },
                    {   text: 'Can Change Data',            value: 'write',             align: 'left',  width:'16%', sortable: true },
                    {   text: 'Can Download Deliverables',  value: 'deliverables',      align: 'left',  width:'20%', sortable: true },
                    {   text: 'Expires',                    value: 'expires',           align: 'left',  width:'16%', sortable: true },
                    {   text: 'Last LogIn',                 value: 'last_logged_in',    align: 'left',  width:'',   sortable: false },
                    {   text: 'Actions',                    value: 'actions',           align: 'left',  width:'8%',  sortable: false },
                ],
                resetCreateNewUser: false,
                secondaryUserDialog: false,
                adminUserDialog: false,
                addUserHeader: 'Add an Owner to this Project',
                userData: {},
                thisUser: {
					user_id: null,
                    newuser_firstname: '',
                    newuser_lastname: '', 
                    newuser_email: '',
                    newuser_type: '',
                    user_permissions: ['r','w'],
                    never_expires: true,
                    expiration_date: ''
                },
                trigger_copied: false
            }     
        },

        mounted () {
            // console.log(this.projectDetails)
        },
        
        methods: {
            handleCopyEmail: function ( id ) {
                navigator.clipboard.writeText(document.querySelector(`span#copy-email-${id}`).innerText.trim())
                this.trigger_copied = true;
                setTimeout(() => { this.trigger_copied = false; }, 3000);
            },

            handleCopyPhone: function ( id ) {
                navigator.clipboard.writeText(document.querySelector(`span#copy-phone-${id}`).innerText.trim())
                this.trigger_copied = true;
                setTimeout(() => { this.trigger_copied = false; }, 3000);
            },

            beginCall: function ( digits ) {
                window.open(`tel:${digits}`)
            },

            beginEmail: function ( address ) {
                window.location.href = `mailto:${address}`;
            },

            closeSecondary: function () {
                this.adminUserDialog = false
                this.secondaryUserDialog = false
				this.resetCreateNewUser = true
            },

            addSecondaryUser: async function ( user_type ) {
                this.thisUser = {
					user_id: null,
                    newuser_firstname: '',
                    newuser_lastname: '', 
                    newuser_email: '',
                    newuser_type: user_type,
                    user_permissions: ['r','w'],
                    never_expires: true,
                    expiration_date: ''
                }
				this.secondaryUserDialog = true
				// this.secondaryProject = prj
			},

            addAdminUser: async function ( user_type ) {
                this.userData.userType = user_type
                this.userData.userGroup = await this.getUserGroup( user_type )

                this.addUserHeader = user_type == 'partner' ? `Add an Partner to this Project` : `Add an Owner to this Project`
                this.adminUserDialog = true
            },

            getUserGroup: async function ( user_type ) {
                switch ( user_type ) {
                    case 'owner':   return [1,2,3,4,6]; break;
                    case 'partner': return [7,8];       break;
                }
            },
            
            editUser: function ( user ) {
                // console.log(user)                
                let user_perms = []
                if ( user.read ) user_perms.push('r')
                if ( user.write ) user_perms.push('w')
                if ( user.deliverables ) user_perms.push('x')
                
                this.thisUser = {
                    id: user.id,
                    user_id: user.user_id,
                    project_id: user.project_id,
                    newuser_firstname: user.first_name,
                    newuser_lastname: user.last_name, 
                    newuser_email: user.user_email,
                    newuser_type: user.user_type,
                    user_permissions: user_perms,
                    never_expires: user.expires == 0 ? true : false,
                    expiration_date: user.expires != 0 ? this.formatExpirationDate(user.expires, true) : ''
                }

                this.secondaryUserDialog = true
			},

            emitUserRefresh: function () {
                this.adminUserDialog = false
                this.secondaryUserDialog = false
                this.resetCreateNewUser = true
                this.$emit('refreshUsers', this.projectDetails.project_id, `${this.thisUser.newuser_firstname} was updated successfully!`)
            },

            deleteUser: async function (user) {
                try {                   
                    const confirm_rs = confirm('Are you sure you want to delete this user from this project?');

                    if ( confirm_rs ) { 
                        const delete_user = await axios.put(`${appUrls.api}/api/delete_project_user`, {
                            headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                            data: {
                                user: user
                            }
                        })

                        if ( delete_user.data.outcome ) {
                            this.projectDetails.users = delete_user.data.users
                            this.$emit('refreshUsers', this.projectDetails.project_id, delete_user.data.message)
                        } else {
                            this.$emit('refreshUsers', this.projectDetails.project_id, delete_user.data.message)
                        }
                    }
                } catch ( err ) {
                    console.log( err )
                }
            },

            formatExpirationDate: function ( date_to_format, iso_format=false ) {
                if ( !date_to_format || date_to_format == 0 ) return false
                
                const options = {
                    year: 'numeric',
                    month: '2-digit', 
                    day: '2-digit'
                }
                const formatted_date = this.formatThisDate(date_to_format * 1000, options, iso_format);
                return formatted_date ? formatted_date : false;
            },

            formatLastLoginDate: function ( date_to_format ) {
                if ( !date_to_format || date_to_format == 0 ) return 'Never Logged In'

                const options = {
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric'
                }

                const formatted_date = this.formatThisDate(date_to_format, options);
                return formatted_date ? formatted_date : '';
            }
        }
    }
</script>

<style scoped>
    .no_expiration {
        font-size: .75rem; 
        padding: 0;
    }
    .menu_bg_primary {
        background-color: #eef7f8 !important;
    }
    .contact_btn_color {
        color: #0095a6c4 !important; 
    }
    .contact_text_link {
        text-decoration: none;
        color: #0096A6;
        cursor: pointer;
    }
    .contact_text_link:hover {
        text-decoration: underline;
    }
    .contact_tooltip {
        font-size: .8rem !important;
        padding: .25rem .8rem !important;
        margin-top: .2rem !important;
    }
</style>



// WEBPACK FOOTER //
// src/components/ProjectUsers.vue